import { Chip, Stack, Typography, styled } from "@mui/material";

import { FONT_WEIGHT_BOLD } from "@/constants/layout";
import type { MenuItem } from "@/types";
import { formatCurrency } from "@/utils/format";

/**
 * `eo.Z` — the shared price block (module 68378), used by all three item tiles
 * and by the offer-reward screens.
 *
 * It is not a Typography: it is a small `Chip` whose label holds the original
 * price struck through beside the price actually charged, optionally preceded by
 * a "Starting From" pill and a discount flash. Rendering the price as plain text
 * (as this reconstruction did) loses the chip surface the tiles are designed
 * around — on REGULAR and SIMPLE it sits directly on the photo.
 */

/** `p` — the "STARTING FROM" pill. */
const Prefix = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "4px",
  padding: "2px 6px",
  width: "fit-content",
  maxWidth: "fit-content",
  minWidth: "auto",
  marginBottom: "4px",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
  display: "inline-block",
  whiteSpace: "nowrap",
}));

/** `u` — the price chip itself. */
const PriceChip = styled(Chip)({
  borderRadius: "5px",
  height: "16px",
  width: "fit-content",
});

/** `x` — the notched discount flash ("20% Off" / "Save Rs 200"). */
const DiscountFlash = styled("div")(({ theme }) => ({
  color: theme.palette.primary.contrastText,
  cursor: "pointer",
  boxSizing: "border-box",
  display: "flex",
  flexDirection: "row",
  padding: "4px 25px 4px 5px",
  width: "fit-content",
  backgroundColor: theme.palette.primary.main,
  borderRadius: 0,
  clipPath: "polygon(100% 0px, 83% 50%, 100% 100%, 0px 100%, 0px 0px)",
  margin: "8px 0px 6px",
}));

interface ItemPriceProps {
  item: MenuItem;
  symbol?: string;
  code?: string;
  color?: "default" | "primary" | "secondary";
}

export default function ItemPrice({
  item,
  symbol,
  code,
  color = "default",
}: ItemPriceProps) {
  const money = (value?: number | null) =>
    formatCurrency(value ?? 0, { symbol, code });

  const original = item.price ?? 0;
  const charged =
    item.discounted_price != null && item.discounted_price < original
      ? item.discounted_price
      : original;

  /**
   * The two discount strings are verbatim: a percentage discount reads
   * `N% Off` and a flat one `Save <amount>`.
   *
   * `discount_amount` is a PLAIN PERCENT here, not basis points — the comment
   * that used to sit here claimed otherwise and the `/ 100` below followed from
   * it, so a 3% discount rendered as "0.03% Off" on six live items. Live data
   * settles it: Seekh Kabab is `{price: 500, discounted_price: 485,
   * discount_amount: 3, is_discount_percentage: true}` — and 485 is exactly 3%
   * off 500, so the rate the API sends is 3, not 300. The original agrees: it
   * renders `"".concat(d, "% Off")` with no division and prices the item as
   * `(l * d) / 100` (recovery-source/live-chunks/fmt_584-d1fb9d663b3e9e21.js
   * :1070 and :3163). Flat discounts were never affected.
   */
  const totalDiscount =
    original > charged
      ? item.is_discount_percentage
        ? `${item.discount_amount ?? 0}% Off`
        : `Save ${money(original - charged)}`
      : null;

  if (original === 0 && charged === 0) return null;

  return (
    <Stack>
      {item.show_starting_from && (
        <Prefix variant="caption" fontWeight={FONT_WEIGHT_BOLD} sx={{ fontSize: "0.65rem" }}>
          Starting From
        </Prefix>
      )}
      {totalDiscount && (
        <DiscountFlash>
          <Typography variant="caption" fontWeight={FONT_WEIGHT_BOLD}>
            {totalDiscount}
          </Typography>
        </DiscountFlash>
      )}
      <PriceChip
        color={color}
        size="small"
        label={
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {original !== charged && (
              <Typography
                variant="body2"
                fontWeight={FONT_WEIGHT_BOLD}
                sx={{ textDecoration: "line-through" }}
                color="text.secondary"
              >
                {money(original)}
              </Typography>
            )}
            <Typography variant="body2" fontWeight={FONT_WEIGHT_BOLD}>
              {money(charged)}
            </Typography>
          </Stack>
        }
      />
    </Stack>
  );
}
